import React from 'react';
import { Box, Container, Tab, Tabs } from '@mui/material';
import { Link, Route, Routes, useLocation } from 'react-router';

import { hasScheduleAccess } from '@paragon/login-tools';
import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';

import { hasCatastrophicFailureOccurred } from '../selectors/catastrophicFailureSelectors';
import { useAppSelector } from './hooks';
import { AssemblyGroupPage } from './pages/AssemblyGroupPage';
import { AssemblyGroupsPage } from './pages/AssemblyGroupsPage';
import { DirectoryPage } from './pages/DirectoryPage';
import { ErrorPage } from './pages/ErrorPage';
import { LinePage } from './pages/LinePage';
import { LinesPage } from './pages/LinesPage';
import { StationPage } from './pages/StationPage';
import { StationsPage } from './pages/StationsPage';
import { TrailersPage } from './pages/TrailersPage';
import { TruckDriversPage } from './pages/TruckDriversPage';
import {
  ASSEMBLY_GROUPS_URL_SLUG,
  DIRECTORIES_URL_SLUG,
  LINES_URL_SLUG,
  STATIONS_URL_SLUG,
  TRAILERS_URL_SLUG,
  TRUCK_DRIVERS_URL_SLUG,
} from './url-slugs';

const ASSEMBLY_GROUPS_TAB_INDEX = 0;
const STATIONS_TAB_INDEX = 1;
const LINES_TAB_INDEX = 2;
const TRAILERS_TAB_INDEX = 3;
const TRUCK_DRIVERS_TAB_INDEX = 4;

export function Content() {
  const spinnerMessages = useAppSelector(getSpinnersToDisplay);
  const failedToLoad = useAppSelector(hasCatastrophicFailureOccurred);
  const userHasScheduleAccess = useAppSelector(hasScheduleAccess);

  const location = useLocation();

  let activeTabIndex = ASSEMBLY_GROUPS_TAB_INDEX;
  if (location.pathname === '/' || location.pathname.includes(ASSEMBLY_GROUPS_URL_SLUG)) {
    activeTabIndex = ASSEMBLY_GROUPS_TAB_INDEX;
  } else if (location.pathname.includes(STATIONS_URL_SLUG)) {
    activeTabIndex = STATIONS_TAB_INDEX;
  } else if (location.pathname.includes(LINES_URL_SLUG)) {
    activeTabIndex = LINES_TAB_INDEX;
  } else if (location.pathname.includes(TRAILERS_URL_SLUG)) {
    activeTabIndex = TRAILERS_TAB_INDEX;
  } else if (location.pathname.includes(TRUCK_DRIVERS_URL_SLUG)) {
    activeTabIndex = TRUCK_DRIVERS_TAB_INDEX;
  }

  return (
    <Box
      component="main"
      sx={{
        padding: 3,
        minWidth: 0, // So the Typography noWrap works
      }}
    >
      {Array.from(spinnerMessages).map((text) => (
        <SpinnerMessage text={text} key={text} />
      ))}

      {failedToLoad ? (
        <ErrorPage
          title="Error &mdash; unable to load"
          body="Refresh to try again, or contact Paragon Customer Support."
        />
      ) : (
        <Container maxWidth="md">
          <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
            <Tabs value={activeTabIndex}>
              <Tab
                tabIndex={ASSEMBLY_GROUPS_TAB_INDEX}
                label="Assembly Groups"
                component={Link}
                to={`/${ASSEMBLY_GROUPS_URL_SLUG}`}
              />
              <Tab tabIndex={STATIONS_TAB_INDEX} label="Stations" component={Link} to={`/${STATIONS_URL_SLUG}`} />
              <Tab tabIndex={LINES_TAB_INDEX} label="Lines" component={Link} to={`/${LINES_URL_SLUG}`} />
              {userHasScheduleAccess && [
                <Tab
                  key="trailers"
                  tabIndex={TRAILERS_TAB_INDEX}
                  label="Trailers"
                  component={Link}
                  to={`/${TRAILERS_URL_SLUG}`}
                />,
                <Tab
                  key="truck-drivers"
                  tabIndex={TRUCK_DRIVERS_TAB_INDEX}
                  label="Truck Drivers"
                  component={Link}
                  to={`/${TRUCK_DRIVERS_URL_SLUG}`}
                />,
              ]}
            </Tabs>
          </Box>

          <Routes>
            <Route path="/" element={<AssemblyGroupsPage />} />
            <Route path={`/${ASSEMBLY_GROUPS_URL_SLUG}`} element={<AssemblyGroupsPage />} />
            <Route path={`/${ASSEMBLY_GROUPS_URL_SLUG}/:assemblyGroupGuid`} element={<AssemblyGroupPage />} />
            <Route path={`/${LINES_URL_SLUG}/:lineGuid`} element={<LinePage />} />
            <Route path={`/${STATIONS_URL_SLUG}/:stationGuid`} element={<StationPage />} />
            <Route path={`/${LINES_URL_SLUG}/:lineGuid/${STATIONS_URL_SLUG}/:stationGuid`} element={<StationPage />} />
            <Route path={`/${DIRECTORIES_URL_SLUG}/:directoryGuid`} element={<DirectoryPage />} />
            <Route path={`/${LINES_URL_SLUG}`} element={<LinesPage />} />
            <Route path={`/${STATIONS_URL_SLUG}`} element={<StationsPage />} />
            {userHasScheduleAccess && [
              <Route key="trailers" path={`/${TRAILERS_URL_SLUG}`} element={<TrailersPage />} />,
              <Route key="truck-drivers" path={`/${TRUCK_DRIVERS_URL_SLUG}`} element={<TruckDriversPage />} />,
            ]}
          </Routes>
        </Container>
      )}
    </Box>
  );
}
